// ── CONCEPTUAL INTERIOR DESIGN FEE ──────────────────────────────────────
//
// Conceptual interior design is sold as a SERVICE, priced per space: the rep
// sets how many spaces and a per-space rate. Bernard, 2026-10-24: $750 per
// space by default, anywhere from $500 to $1,000. Stored on
// `inputs.conceptualDesignFee` as { spaces, rate, amount, note }. The note is
// optional and names the spaces ("Lobby, amenity lounge, model unit").
//
// Quotes saved before per-space pricing carry a FLAT fee, { amount, note } and
// no `spaces` (Jordan's "$15k for unit finishes + the commercial space"). Those
// keep billing exactly what was sent. They are never re-priced as spaces.
//
// It is the mirror image of the additional discount (./proposalDiscount): its
// own printed line under the proposal's Conceptual Design heading, never
// folded into vizNet or the bundle, and it is ADDED to:
//   • oneTimeAllIn / grandTotal on the calc (so every total reads it), and
//   • the visualization split the payment plan divides (vizSubtotal), so the
//     milestones sum to the one-time total with the fee in it.
// The backend mirror is `conceptual_design_fee_of()` in
// backend/services/vexos_payment_plan.py, which adds the saved
// `calc_snapshot.conceptualDesignFee` inside plan_from_quote().
//
// Applied after the additional discount, so a % discount is never taken off
// the fee.

export const CONCEPTUAL_DESIGN_FEE_LABEL = "Conceptual design";
export const CONCEPTUAL_DESIGN_FEE_DEFAULT_NOTE = "Design development · flat fee";
export const INTERIOR_DESIGN_LABEL = "Conceptual interior design";

export const INTERIOR_DESIGN_RATE_DEFAULT = 750;
export const INTERIOR_DESIGN_RATE_MIN = 500;
export const INTERIOR_DESIGN_RATE_MAX = 1000;
export const INTERIOR_DESIGN_MAX_SPACES = 99;

// What TRP does and does not do. Printed under the line on the proposal and
// shown to the rep on the card. Matches the Project Intake Guide, where the
// interior design package is an "if applicable" file with its own fee.
// Mirrored in vexos_payment_plan.py. DRAFT, to be reviewed by Gonzalo.
export const INTERIOR_DESIGN_SCOPE = {
  included:
    "For each space without an interior design package, our design team creates the look: furniture layout, finishes and materials, lighting and styling. You approve the direction before we render.",
  excluded:
    "Design for the renderings, not for construction. We are not your interior designer of record: no construction drawings, specifications, FF&E schedules or purchasing lists.",
};

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const money = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

/** A per-space rate, held inside the $500–$1,000 range. */
export function clampInteriorDesignRate(raw) {
  const n = Math.round(num(raw));
  if (n <= 0) return INTERIOR_DESIGN_RATE_DEFAULT;
  return Math.min(INTERIOR_DESIGN_RATE_MAX, Math.max(INTERIOR_DESIGN_RATE_MIN, n));
}

/** The persisted value, or null when there is no fee. */
export function normalizeConceptualDesignFee(raw) {
  if (!raw || typeof raw !== "object") return null;
  const note = typeof raw.note === "string" ? raw.note.trim().slice(0, 140) : "";
  if (raw.spaces != null) {
    const spaces = Math.min(INTERIOR_DESIGN_MAX_SPACES, Math.round(num(raw.spaces)));
    if (spaces <= 0) return null;
    const rate = clampInteriorDesignRate(raw.rate);
    return { spaces, rate, amount: spaces * rate, note };
  }
  // Legacy flat fee.
  const amount = Math.round(num(raw.amount));
  if (amount <= 0) return null;
  return { amount, note };
}

/** The calc with the fee stamped on it and added to its totals. */
export function applyConceptualDesignFee(calc, fee) {
  const f = normalizeConceptualDesignFee(fee);
  if (!f) {
    return { ...calc, conceptualDesignFee: 0, conceptualDesignFeeNote: "", conceptualDesignFeeSpaces: 0, conceptualDesignFeeRate: 0 };
  }
  return {
    ...calc,
    conceptualDesignFee: f.amount,
    conceptualDesignFeeNote: f.note,
    conceptualDesignFeeSpaces: f.spaces || 0,
    conceptualDesignFeeRate: f.rate || 0,
    oneTimeAllIn: num(calc.oneTimeAllIn) + f.amount,
    grandTotal: num(calc.grandTotal) + f.amount,
  };
}

/** Line label: per-space fees are interior design; legacy flat fees keep theirs. */
export function conceptualDesignFeeLabel(spaces) {
  return num(spaces) > 0 ? INTERIOR_DESIGN_LABEL : CONCEPTUAL_DESIGN_FEE_LABEL;
}

/** "3 spaces × $750 per space", or "" for a flat fee. */
export function interiorDesignMath(spaces, rate) {
  const s = Math.round(num(spaces));
  if (s <= 0) return "";
  return `${s} space${s === 1 ? "" : "s"} × ${money(num(rate))} per space`;
}

/**
 * Sub-line printed under the fee. Per-space: the math, then the rep's note.
 * Flat: the rep's note, else a plain default.
 */
export function conceptualDesignFeeSub(note, spaces = 0, rate = 0) {
  const text = (typeof note === "string" && note.trim()) || "";
  const math = interiorDesignMath(spaces, rate);
  if (math) return text ? `${math} · ${text}` : math;
  return text || CONCEPTUAL_DESIGN_FEE_DEFAULT_NOTE;
}
